/**
 * Ops health board contracts, shared by the tenant HealthSentinel, ops-api and
 * the ops SPA. Check ids are persisted in HealthTransition.checkId — never
 * rename one; add a new id instead.
 */

export const HealthStatus = {
  Ok: 'ok',
  Warn: 'warn',
  Fail: 'fail',
  Unknown: 'unknown',
} as const;
export type HealthStatus = (typeof HealthStatus)[keyof typeof HealthStatus];

export type HealthGroup = 'core' | 'workers' | 'queues' | 'runs' | 'upstreams' | 'host' | 'data';

/** Every check on the board, in display order. */
export const HEALTH_CHECKS = [
  { id: 'core.database', group: 'core', label: 'Database' },
  { id: 'core.redis', group: 'core', label: 'Redis' },
  { id: 'core.sentinel', group: 'core', label: 'Health sentinel' },
  { id: 'core.version', group: 'core', label: 'Version' },
  { id: 'worker.loop-scheduler', group: 'workers', label: 'Loop scheduler' },
  { id: 'worker.dispatch-reconcile', group: 'workers', label: 'Reconcile sweep' },
  { id: 'worker.workspace-reaper', group: 'workers', label: 'Reaper' },
  { id: 'worker.pr-sync', group: 'workers', label: 'PR sync' },
  { id: 'worker.image-janitor', group: 'workers', label: 'Image janitor' },
  { id: 'worker.error-watcher', group: 'workers', label: 'Prototype error watcher' },
  { id: 'worker.traffic', group: 'workers', label: 'Traffic sampler' },
  { id: 'worker.prototype-sleep', group: 'workers', label: 'Prototype sleep' },
  { id: 'worker.integration-webhooks', group: 'workers', label: 'Integration webhooks' },
  { id: 'worker.daily-summary', group: 'workers', label: 'Daily summary' },
  { id: 'queue.agent-runs', group: 'queues', label: 'Agent run queue' },
  { id: 'queue.prototype-deploys', group: 'queues', label: 'Prototype deploy queue' },
  { id: 'queue.dispatch', group: 'queues', label: 'Reactive dispatch' },
  { id: 'runs.success-rate', group: 'runs', label: 'Success rate (24 h)' },
  { id: 'runs.stuck', group: 'runs', label: 'Stuck runs' },
  { id: 'runs.oom', group: 'runs', label: 'OOM kills (24 h)' },
  { id: 'runs.orphaned', group: 'runs', label: 'Orphaned runs (24 h)' },
  { id: 'runs.delivery', group: 'runs', label: 'Delivery failures (24 h)' },
  { id: 'runs.eligible-waiting', group: 'runs', label: 'Eligible runs waiting' },
  { id: 'upstream.claude', group: 'upstreams', label: 'Claude subscription' },
  { id: 'upstream.github', group: 'upstreams', label: 'GitHub App' },
  { id: 'upstream.domains', group: 'upstreams', label: 'Custom domains' },
  { id: 'upstream.llm', group: 'upstreams', label: 'Claude API (server features)' },
  { id: 'host.memory', group: 'host', label: 'Memory available' },
  { id: 'host.disk', group: 'host', label: 'Disk free' },
  { id: 'host.prototypes', group: 'host', label: 'Prototype fleet' },
  { id: 'data.backup', group: 'data', label: 'Nightly backup' },
] as const satisfies readonly { id: string; group: HealthGroup; label: string }[];

export type HealthCheckId = (typeof HEALTH_CHECKS)[number]['id'];

/** Checks ops-api evaluates live on each request; the sentinel evaluates the rest. */
export const OPS_HEALTH_CHECK_IDS = [
  'core.database',
  'core.redis',
  'core.sentinel',
  'core.version',
  'data.backup',
] as const satisfies readonly HealthCheckId[];
export type OpsHealthCheckId = (typeof OPS_HEALTH_CHECK_IDS)[number];
export type SentinelHealthCheckId = Exclude<HealthCheckId, OpsHealthCheckId>;
export type WorkerHealthCheckId = Extract<HealthCheckId, `worker.${string}`>;

/** True for ids ops-api evaluates live (vs. the tenant sentinel). */
export function isOpsHealthCheckId(id: string): id is OpsHealthCheckId {
  return (OPS_HEALTH_CHECK_IDS as readonly string[]).includes(id);
}

const ALL_HEALTH_CHECK_IDS: readonly string[] = HEALTH_CHECKS.map((c) => c.id);

/** True for any id in the catalogue (e.g. a route param). */
export function isHealthCheckId(id: string): id is HealthCheckId {
  return ALL_HEALTH_CHECK_IDS.includes(id);
}

export const SENTINEL_HEALTH_CHECK_IDS: readonly SentinelHealthCheckId[] = HEALTH_CHECKS.map(
  (c) => c.id,
).filter((id): id is SentinelHealthCheckId => !isOpsHealthCheckId(id));

export const WORKER_HEALTH_CHECK_IDS: readonly WorkerHealthCheckId[] = HEALTH_CHECKS.map(
  (c) => c.id,
).filter((id): id is WorkerHealthCheckId => id.startsWith('worker.'));

export interface HealthCheckDto {
  id: HealthCheckId;
  group: HealthGroup;
  label: string;
  status: HealthStatus;
  reason: string;
  /** ISO time it entered this status; null when unknown (ops checks, first sight). */
  since: string | null;
}

export interface HealthTransitionDto {
  checkId: HealthCheckId;
  from: HealthStatus;
  to: HealthStatus;
  reason: string;
  at: string;
}

/** `GET /api/admin/health` (ops plane). */
export interface HealthBoardDto {
  overall: HealthStatus;
  /** When ops assembled this response. */
  generatedAt: string;
  sentinelGeneratedAt: string | null;
  version: { apiSha: string | null; apiBuiltAt: string | null; opsSha: string | null };
  /** Catalogue order. */
  checks: HealthCheckDto[];
  /** Last 24 h, newest first. */
  transitions: HealthTransitionDto[];
  /** Acknowledgements still in force (see OpsHealthAcksService); absent id = not acknowledged. */
  acks: Partial<Record<HealthCheckId, HealthAckDto>>;
  /** From the sentinel snapshot; null when it has not reported one. */
  agentCapacity: AgentCapacity | null;
}

/** An operator's "seen it" on a warn/fail check; it lapses on a new item, a worse status or a new episode. */
export interface HealthAckDto {
  at: string;
  /** Operator email. */
  by: string;
  note: string | null;
  /** The check's status when acknowledged. */
  status: HealthStatus;
  /** The check's `since` when acknowledged: a different value means a new episode. */
  since: string | null;
}

export interface HealthDetailFact {
  label: string;
  value: string;
}

/** One concrete thing behind a check's count: a run, a job, a domain, a dropped signal. */
export interface HealthDetailItem {
  id: string;
  /** ISO time it happened, when known. */
  at: string | null;
  title: string;
  detail: string | null;
  workspace: { id: string; name: string } | null;
  /** The run behind this item, when there is one: the console links it to its detail page. */
  runId?: string;
  /** The prototype (project id) behind this item, when there is one: the console links it to its ops page. */
  prototypeProjectId?: string;
  facts: HealthDetailFact[];
}

/** `GET /api/admin/health/checks/:id` (ops plane): what is behind a check, newest first. */
export interface HealthCheckDetailDto {
  checkId: HealthCheckId;
  /** A data-driven one-liner (e.g. "host memory pressure, not the container limit"), or null. */
  diagnosis: string | null;
  items: HealthDetailItem[];
  /** More items exist than were returned. */
  truncated: boolean;
  /** Set when the details could not be loaded; items is then empty. */
  error: string | null;
}

/** One run the reconcile sweep enqueued because the reactive path missed its signal. */
export interface ReconcileCatch {
  at: string;
  orgId: string;
  projectId: string;
  lane: 'plan' | 'build' | 'chat';
}

/** How many reconcile catches the tenant keeps in Redis (newest first). */
export const RECONCILE_CATCH_KEEP = 200;

/** Parse one `HEALTH_REDIS_KEYS.dispatchCatches` entry; null when corrupt or wrongly shaped. */
export function parseReconcileCatch(raw: string): ReconcileCatch | null {
  try {
    const v = JSON.parse(raw) as Partial<ReconcileCatch> | null;
    if (
      !v ||
      typeof v.at !== 'string' ||
      typeof v.orgId !== 'string' ||
      typeof v.projectId !== 'string' ||
      (v.lane !== 'plan' && v.lane !== 'build' && v.lane !== 'chat')
    ) {
      return null;
    }
    return { at: v.at, orgId: v.orgId, projectId: v.projectId, lane: v.lane };
  } catch {
    return null;
  }
}

/** A pending run that could start (project idle, workspace under its cap, no pursued-PR blocker) while a slot was free. */
export interface EligibleWaitingRun {
  runId: string;
  orgId: string;
  projectId: string;
  lane: 'plan' | 'build' | 'chat';
  /** ISO time the sentinel first saw it eligible with a free slot. */
  eligibleSince: string;
}

/** How long an eligible run may wait with a free slot before `runs.eligible-waiting` flags it. */
export const ELIGIBLE_RUN_WAIT_MS = 5 * 60_000;

/** The agent-run worker's capacity, as the tenant sentinel last measured it. */
export interface AgentCapacity {
  /** The resolved global `agent.maxConcurrency` (worker slots). */
  maxConcurrency: number;
  /** Runs provisioning/running in the database, all workspaces. */
  activeRuns: number;
  /** Running agent containers on the Docker daemon; null when the daemon could not be read. */
  activeContainers: number | null;
  /** BullMQ job counts of the agent-run queue; null when Redis could not be read. */
  queue: { active: number; waiting: number; delayed: number } | null;
  /** Runs eligible to start while a slot was free, longest-waiting first. */
  eligibleWaiting: EligibleWaitingRun[];
}

/** The sentinel's Redis payload under HEALTH_REDIS_KEYS.snapshot. */
export interface HealthSnapshot {
  generatedAt: string;
  apiSha: string | null;
  apiBuiltAt: string | null;
  sentinelStartedAt: string;
  checks: HealthCheckDto[];
  /** Absent on snapshots written before it existed; null when it could not be measured. */
  agentCapacity?: AgentCapacity | null;
}

export const HEALTH_REDIS_KEYS = {
  snapshot: 'health:snapshot',
  sentinelLock: 'health:sentinel:lock',
  githubApp: 'health:github-app',
  backupFailure: 'health:backup:lastFailure',
  heartbeat: (id: WorkerHealthCheckId) => `health:hb:${id}`,
  /** Hash checkId → JSON HealthAckDto (written by ops-api). */
  acks: 'health:acks',
  /** List of JSON ReconcileCatch, newest first, capped at RECONCILE_CATCH_KEEP (written by the tenant). */
  dispatchCatches: 'health:dispatch:catches',
} as const;

const RANK: Record<HealthStatus, number> = { ok: 0, unknown: 1, warn: 2, fail: 3 };

/** Severity order: ok 0 < unknown 1 < warn 2 < fail 3. */
export function healthStatusRank(s: HealthStatus): number {
  return RANK[s];
}

/** The overall status: fail > warn > unknown > ok. Empty → ok. */
export function worstHealthStatus(statuses: readonly HealthStatus[]): HealthStatus {
  return statuses.reduce<HealthStatus>((worst, s) => (RANK[s] > RANK[worst] ? s : worst), 'ok');
}

/** Compact age: "45s", "12m", "3h 5m", "2d 4h". Negative → "0s". */
export function formatHealthDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
}

/** Parse the Redis snapshot; null for absent, corrupt or wrongly shaped input. */
export function parseHealthSnapshot(raw: string | null): HealthSnapshot | null {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Partial<HealthSnapshot> | null;
    if (!s || typeof s.generatedAt !== 'string' || !Array.isArray(s.checks)) return null;
    return s as HealthSnapshot;
  } catch {
    return null;
  }
}
